import { chain, filter } from "lodash"
import { generateGathererKeys } from "connected-components/dashboard/helpers/query-key"
import { buildContributorKey } from "helpers/contributors"
import { getContributorLabel, NOT_HELD } from "../../connected-components/asset-view/helpers"
import { filterOptionsByCategory, getFactorValueByMetricDisplay } from "./helpers/factor-utils"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet } from "../helpers"

const CONTRIBUTORS_SUBQUERY = `
contributors {
  id
  description
  modelProviderId
  assetClass
  notHeld
  factors {
    id
    category
    netContribution
    currencyExposure
    securityExposure
  }
}`

function getUngroupedContributorsSubQuery() {
  return `
    exposureContributors (includeNotHeld: $includeNotHeld) {
        ${CONTRIBUTORS_SUBQUERY}
    }
  `
}

function getGroupedContributorsSubQuery(groupByFields) {
  return `groupedExposureContributors (${getGroupBySubqueryParameters(groupByFields)}, includeNotHeld: $includeNotHeld) {
    groupedContributors {
      id
      name
      factors {
        id
        category
        netContribution
        currencyExposure
        netExposure
      }
      ${CONTRIBUTORS_SUBQUERY}
    }
  }`
}

function getQuery(groupByFields, shouldUseIntradayQuery) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""
  const innerQuery = `exposure {
              factors {
                id
                category
                netContribution: net
                currencyExposure
              }
            }
            ${groupByFields ? getGroupedContributorsSubQuery(groupByFields) : getUngroupedContributorsSubQuery()}
            `

  if (shouldUseIntradayQuery) {
    return `query ExposureContributorsQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $includeNotHeld: Boolean!${extraParameters}, $batchId: Id) {
        model (id: $modelId) {
          simulation:intradaySimulationBatch (positionSet: $positionSet, on: $on, batchId: $batchId, base: $base) {
            ${innerQuery}
          }
        }
      }`
  }

  return `query ExposureContributorsQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $date: Date!, $includeNotHeld: Boolean!${extraParameters}) {
        model (id: $modelId) {
          simulation (positionSet: $positionSet, base: $base, from: $date, to: $date) {
            ${innerQuery}
          }
        }
      }`
}

function parseContributors(prefix, contributors, options) {
  if (!contributors) {
    return {}
  }
  return contributors?.reduce((acc, contributor) => {
    if (contributor.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: {},
          },
        }
      }
      chain(contributor.factors)
        .thru(factors => {
          if (options.category) {
            return filter(factors, f => f.category === options.category)
          }
          return factors
        })
        .forEach(factor => {
          if (!acc[NOT_HELD].values[prefix][factor.id]) {
            acc[NOT_HELD].values[prefix][factor.id] = getFactorValueByMetricDisplay(factor, options.selectedMetricDisplay)
          } else {
            acc[NOT_HELD].values[prefix][factor.id] += getFactorValueByMetricDisplay(factor, options.selectedMetricDisplay)
          }
        })
        .value()
    } else {
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld,
        values: {
          [prefix]: chain(contributor.factors)
            .thru(factors => {
              if (options.category) {
                return filter(factors, f => f.category === options.category)
              }
              return factors
            })
            .keyBy("id")
            .mapValues(factor => getFactorValueByMetricDisplay(factor, options.selectedMetricDisplay))
            .value(),
        },
      }
    }
    return acc
  }, {})
}

function parseGroupedContributors(prefix, groups, options) {
  if (!groups) {
    return {}
  }
  return chain(groups)
    .keyBy("id")
    .mapValues(group => ({
      label: group.name,
      values: {
        [prefix]: chain(group.factors)
          .thru(factors => {
            if (options.category) {
              return filter(factors, f => f.category === options.category)
            }
            return factors
          })
          .keyBy("id")
          .mapValues(factor => getFactorValueByMetricDisplay(factor, options.selectedMetricDisplay))
          .value(),
      },
      children: parseContributors(prefix, group.contributors, options),
    }))
    .value()
}

export function createExposureContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const { batchId, on } = options
  const shouldUseIntradayQuery = Boolean(options.shouldUseIntradayQuery)

  return {
    prefix,
    ...generateGathererKeys("exposure.contributors", {
      prefix, modelId, positionSet, base, to,
      groupBy, groupById, tier,
      intraday: shouldUseIntradayQuery || undefined,
      batchId, on,
    }),
    query: getQuery(groupBy && groupByFields, shouldUseIntradayQuery),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      batchId: batchId,
      on,
      base,
      date: to,
      ...groupByFields,
      includeNotHeld: !!base,
    },
    select: (data) => {
      const total = chain(data)
        .get("data.model.simulation.exposure.0.factors")
        .thru(factors => filterOptionsByCategory(factors, options.category))
        .keyBy("id")
        .mapValues(factor => getFactorValueByMetricDisplay(factor, options.selectedMetricDisplay))
        .value()

      const children = groupBy
        ? parseGroupedContributors(prefix, data.data.model.simulation.groupedExposureContributors?.[0]?.groupedContributors, options)
        : parseContributors(prefix, data.data.model.simulation.exposureContributors?.[0]?.contributors, options)

      return {
        label: "Total",
        values: {
          [prefix]: total,
        },
        children,
      }
    },
  }
}
